import { useQuery } from '@apollo/client';
import { fyoogaQueries, fyoogaSubscriptions } from '@fyooga/graphql-requests';
import { useEffect } from 'react';

import { OfficeEmployeeT, OfficeT } from '../../../types/office';

type OfficeByDomainT = {
    data: {
        office: OfficeT;
        showCourses: boolean;
        showWorkshops: boolean;
    } | null;
    loading: boolean;
};

/**
 * hook used for fetching public info about office, studioDomain must be param in path
 */
export const useOfficeByDomain = (domainToQuery: string, isPublic: boolean): OfficeByDomainT => {
    const {
        subscribeToMore,
        loading: loadingOfficeData,
        data: officeData,
    } = useQuery(fyoogaQueries.getOfficeByDomain, {
        variables: {
            domain: domainToQuery,
        },
    });

    const { data: showCoursesAndWorkshopsData } = useQuery(fyoogaQueries.getShowCoursesAndWorkshopsForOfficeId, {
        variables: {
            officeId: officeData?.officeByDomain?.officeId,
        },
        skip: !officeData?.officeByDomain?.officeId,
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (officeData?.officeByDomain?.officeId) {
            const unsubscribe = subscribeToMore({
                document: fyoogaSubscriptions.tariffLimitsUpdate,
                variables: { officeId: officeData.officeByDomain.officeId },
                updateQuery: (currentlyCachedResultData, { subscriptionData }) => {
                    if (!subscriptionData.data) {
                        return currentlyCachedResultData;
                    }

                    const newTariffLimits = subscriptionData.data.tariffLimitsUpdated;

                    const { limits, officeId: newTariffLimitsOfficeId } = newTariffLimits;

                    const officeByDomainData = currentlyCachedResultData.officeByDomain;

                    if (officeData?.officeByDomain?.officeId === newTariffLimitsOfficeId) {
                        return {
                            officeByDomain: {
                                ...officeByDomainData,
                                tariffLimits: limits,
                                orgTariffMeta: {
                                    tariffStatus: limits.organisationTariff.tariffStatus,
                                    tariffName: limits.organisationTariff.name,
                                    studio: limits.bookTariff.studio,
                                },
                            },
                        };
                    } else {
                        return {
                            officeByDomain: officeByDomainData,
                        };
                    }
                },
            });
            return () => unsubscribe();
        }
    }, [officeData?.officeByDomain?.officeId]);

    if (loadingOfficeData) {
        return { loading: true, data: null };
    }

    return {
        data: {
            office: officeData?.officeByDomain,
            showWorkshops: showCoursesAndWorkshopsData?.showCoursesAndWorkshopsForOfficeId?.showWorkshops ?? false,
            showCourses: showCoursesAndWorkshopsData?.showCoursesAndWorkshopsForOfficeId?.showCourses ?? false,
        },
        loading: false,
    };
};
